import { apiService } from './api';
import { 
  ClientPlan, 
  ClientPlanWithSites, 
  CreateClientPlanDto, 
  UpdateClientPlanDto, 
  PlanType 
} from '../types/planning';

interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface GetPlansFilters {
  clientId?: string;
  projectId?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'COMPLETED';
  planType?: PlanType;
  page?: number;
  limit?: number;
}

class ClientPlanService {
  /**
   * Get all plans with optional filters
   */
  async getPlans(filters: GetPlansFilters = {}): Promise<PaginatedResponse<ClientPlan>> {
    const response = await apiService.get<PaginatedResponse<ClientPlan>>('/client-plans', filters);
    return response;
  }

  /**
   * Get a single plan by ID with sites
   */
  async getPlanById(id: string): Promise<ClientPlanWithSites> {
    const response = await apiService.get<ApiResponse<ClientPlanWithSites>>(`/client-plans/${id}`);
    return response.data;
  }

  /**
   * Create a new plan
   */
  async createPlan(data: CreateClientPlanDto): Promise<ClientPlan> {
    const response = await apiService.post<ApiResponse<ClientPlan>>('/client-plans', data);
    return response.data;
  }

  /**
   * Update an existing plan
   */
  async updatePlan(id: string, data: UpdateClientPlanDto): Promise<ClientPlan> {
    const response = await apiService.put<ApiResponse<ClientPlan>>(`/client-plans/${id}`, data);
    return response.data;
  }

  /**
   * Mark a workflow saved order as fulfilled after checkout (or when stale).
   * Prefer COMPLETED status so the draft drops out of ACTIVE lists; fall back to delete.
   */
  async fulfillSavedOrder(id: string): Promise<void> {
    try {
      await this.updatePlan(id, { status: 'COMPLETED' });
      return;
    } catch {
      /* fall through */
    }
    try {
      await this.deletePlan(id);
    } catch {
      /* best-effort cleanup */
    }
  }

  /**
   * Delete a plan
   */
  async deletePlan(id: string): Promise<void> {
    await apiService.delete(`/client-plans/${id}`);
  }

  /**
   * Add sites to a plan
   */
  async addSitesToPlan(planId: string, siteIds: string[]): Promise<{ addedCount: number }> {
    const response = await apiService.post<ApiResponse<{ addedCount: number }>>(
      `/client-plans/${planId}/sites`,
      { siteIds }
    );
    return response.data;
  }

  /**
   * Remove a site from a plan
   */
  async removeSiteFromPlan(planId: string, siteId: string): Promise<void> {
    await apiService.delete(`/client-plans/${planId}/sites/${siteId}`);
  }

  /**
   * Get all sites for a specific plan
   */
  async getPlanSites(planId: string): Promise<ApiResponse<any[]>> {
    const response = await apiService.get<ApiResponse<any[]>>(`/client-plans/${planId}/sites`);
    return response;
  }
}

export const clientPlanService = new ClientPlanService();
